import { createStore, reconcile } from "solid-js/store";
import { getApiBaseUrl } from "./config";
import {
  decodeCells,
  decodeHealth,
  decodeStats,
  decodeTrip,
  createTripsApi,
  type TripsApi,
} from "./services/api";
import { mergeHealth } from "./lib/health";
import { clearLegacySettings, loadSettings, saveSettings } from "./services/settings";
import { appendTrip, emptyCells, emptyStats, emptyTrip, mergeCells, mergeStats } from "./state/tripData";
import type {
  CellOverlay,
  HealthSample,
  RequestStatus,
  SettingKey,
  Settings,
  TripDetails,
  TripId,
  TripStats,
  TripSummary,
} from "./types";

export type LiveStatus = "idle" | "connecting" | "connected" | "reconnecting";
export interface AppState {
  trips: TripSummary[];
  trip: TripDetails;
  activeTripId: TripId | null;
  settings: Settings;
  status: RequestStatus;
  liveStatus: LiveStatus;
  error: string | null;
  cellOverlay: CellOverlay;
  cellStatus: RequestStatus;
  stats: TripStats;
  statsStatus: RequestStatus;
  health: HealthSample[];
  healthRevision: bigint;
  isBlockingLoad: boolean;
  tripRevision: number;
}
export interface AppStoreOptions {
  api?: TripsApi;
  storage?: Storage;
  websocketFactory?: (url: string) => WebSocket;
  reconnectBaseMs?: number;
}

export function createAppStore(options: AppStoreOptions = {}) {
  const api = options.api ?? createTripsApi(getApiBaseUrl());
  const storage = options.storage ?? window.localStorage;
  const websocketFactory = options.websocketFactory ?? ((url) => new WebSocket(url));
  const reconnectBaseMs = options.reconnectBaseMs ?? 1_000;
  clearLegacySettings(storage);
  const [state, setState] = createStore<AppState>({
    trips: [],
    trip: emptyTrip(),
    activeTripId: null,
    settings: loadSettings(storage),
    status: "idle",
    liveStatus: "idle",
    error: null,
    cellOverlay: emptyCells(),
    cellStatus: "idle",
    stats: emptyStats(),
    statsStatus: "idle",
    health: [],
    healthRevision: 0n,
    isBlockingLoad: false,
    tripRevision: 0,
  });
  let tripController: AbortController | undefined, listController: AbortController | undefined;
  let socket: WebSocket | undefined, reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  let reconnectAttempt = 0,
    blockingVersion = 0,
    selectionVersion = 0,
    disposed = false;
  const beginBlockingLoad = () => {
    const version = ++blockingVersion;
    setState("isBlockingLoad", true);
    return version;
  };
  const endBlockingLoad = (version: number) => {
    if (version === blockingVersion) setState("isBlockingLoad", false);
  };
  const stopLive = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = undefined;
    }
    const previous = socket;
    socket = undefined;
    if (previous && previous.readyState < WebSocket.CLOSING) previous.close(1000, "subscription changed");
    setState("liveStatus", "idle");
  };
  const scheduleReconnect = (id: TripId, selected: number) => {
    if (disposed || selected !== selectionVersion || state.activeTripId !== id) return;
    setState("liveStatus", "reconnecting");
    if (!navigator.onLine) return;
    const delay = Math.min(30_000, reconnectBaseMs * 2 ** Math.min(reconnectAttempt++, 5));
    const jittered = Math.round(delay * (0.8 + Math.random() * 0.4));
    reconnectTimer = setTimeout(() => connectLive(id, selected), jittered);
  };
  const connectLive = (id: TripId, selected: number) => {
    if (disposed || selected !== selectionVersion || state.activeTripId !== id) return;
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = undefined;
    }
    setState("liveStatus", reconnectAttempt ? "reconnecting" : "connecting");
    let current: WebSocket;
    const wantsCells =
      state.settings.showPoints ||
      state.settings.showTowers ||
      state.settings.showLines ||
      state.settings.showCellCoverage;
    try {
      current = websocketFactory(
        api.getLiveUrl(id, {
          after: state.trip.cursor,
          cellsAfter: wantsCells && state.cellStatus === "ready" ? state.cellOverlay.cursor : undefined,
          statsAfter: state.settings.showStats ? state.stats.cursor : undefined,
          healthAfter: state.healthRevision,
        }),
      );
    } catch {
      scheduleReconnect(id, selected);
      return;
    }
    socket = current;
    current.binaryType = "arraybuffer";
    current.onopen = () => {
      if (socket === current) {
        reconnectAttempt = 0;
        setState("liveStatus", "connected");
      }
    };
    current.onmessage = (event) => {
      if (socket !== current || selected !== selectionVersion || !(event.data instanceof ArrayBuffer)) return;
      const magic = String.fromCharCode(...new Uint8Array(event.data, 0, 4));
      if (magic === "HLTH") {
        try {
          const delta = decodeHealth(event.data);
          if (delta.revision <= state.healthRevision) return;
          if (!delta.delta || delta.base_revision !== state.healthRevision) {
            current.close(1008, "health revision gap");
            return;
          }
          setState("health", reconcile(mergeHealth(state.health, delta.samples, delta.deletions)));
          setState("healthRevision", delta.revision);
        } catch {
          current.close(1002, "invalid health delta");
        }
        return;
      }
      if (magic === "CELL") {
        try {
          const delta = decodeCells(event.data);
          if (delta.cursor <= state.cellOverlay.cursor) return;
          if (delta.delta && delta.baseCursor !== state.cellOverlay.cursor) {
            current.close(1008, "cell cursor gap");
            return;
          }
          setState("cellOverlay", reconcile(mergeCells(state.cellOverlay, delta)));
          setState("cellStatus", "ready");
        } catch {
          current.close(1002, "invalid cell delta");
        }
        return;
      }
      if (magic === "STAT") {
        try {
          const delta = decodeStats(event.data);
          if (delta.cursor < state.stats.cursor) return;
          if (delta.delta && delta.baseCursor !== state.stats.cursor) {
            current.close(1008, "stats cursor gap");
            return;
          }
          const next = mergeStats(state.stats, delta);
          setState("stats", reconcile(next));
          setState("statsStatus", "ready");
        } catch {
          current.close(1002, "invalid stats delta");
        }
        return;
      }
      let delta: TripDetails;
      try {
        delta = decodeTrip(event.data);
      } catch {
        current.close(1002, "invalid trip delta");
        return;
      }
      if (
        !delta.delta ||
        delta.cursor < state.trip.cursor ||
        (delta.cursor === state.trip.cursor && !delta.finished)
      )
        return;
      if (delta.baseCursor !== state.trip.cursor) {
        current.close(1008, "cursor gap");
        return;
      }
      const next = appendTrip(state.trip, delta);
      setState("trip", reconcile(next));
      setState({ tripRevision: state.tripRevision + 1, error: null });
    };
    current.onclose = () => {
      if (socket !== current) return;
      socket = undefined;
      scheduleReconnect(id, selected);
    };
    current.onerror = () => {
      if (socket === current) current.close();
    };
  };
  const restartLive = () => {
    const id = state.activeTripId;
    if (id === null) return;
    stopLive();
    connectLive(id, selectionVersion);
  };
  let cellController: AbortController | undefined;
  let statsController: AbortController | undefined;
  const loadStats = async () => {
    const id = state.activeTripId;
    if (id === null) return;
    if (!api.getStats) {
      setState("statsStatus", "ready");
      return;
    }
    statsController?.abort();
    const controller = new AbortController();
    statsController = controller;
    setState("statsStatus", "loading");
    try {
      const stats = await api.getStats(id, state.trip.cursor, controller.signal);
      if (controller.signal.aborted || state.activeTripId !== id) return;
      setState("stats", reconcile(stats));
      setState("statsStatus", "ready");
    } catch {
      if (!controller.signal.aborted) setState("statsStatus", "error");
    }
  };
  const loadHealth = async () => {
    const id = state.activeTripId;
    if (id === null || !api.getHealth) return;
    try {
      const snapshot = await api.getHealth(id);
      if (state.activeTripId === id) {
        setState("health", reconcile(snapshot.samples));
        setState("healthRevision", snapshot.revision);
      }
    } catch {
      // Health data is optional and must not prevent a trip from loading.
    }
  };
  const loadCells = async () => {
    const id = state.activeTripId;
    if (id === null || state.cellStatus === "loading") return;
    cellController?.abort();
    const controller = new AbortController();
    cellController = controller;
    setState("cellStatus", "loading");
    try {
      const overlay = await api.getCells(id, state.trip.cursor, controller.signal);
      if (controller.signal.aborted || state.activeTripId !== id) return;
      setState("cellOverlay", reconcile(overlay));
      setState("cellStatus", "ready");
      restartLive();
    } catch (error) {
      if (!controller.signal.aborted) {
        setState("cellStatus", "error");
        setState("error", error instanceof Error ? error.message : "Cell data failed");
      }
    }
  };
  const refreshTrip = async (snapshot = false): Promise<void> => {
    const id = state.activeTripId;
    if (id === null) return;
    tripController?.abort();
    const controller = new AbortController();
    tripController = controller;
    try {
      const trip = await api.getTrip(id, snapshot ? undefined : state.trip.cursor, controller.signal);
      if (!controller.signal.aborted && state.activeTripId === id && trip) {
        const next = snapshot
          ? trip
          : trip.baseCursor === state.trip.cursor
            ? appendTrip(state.trip, trip)
            : trip;
        setState("trip", reconcile(next));
        setState({ status: "ready", error: null, tripRevision: state.tripRevision + 1 });
      } else if (!controller.signal.aborted) setState("status", "ready");
    } catch (error) {
      if (!controller.signal.aborted)
        setState({ status: "error", error: error instanceof Error ? error.message : "Request failed" });
    }
  };
  const selectTripInternal = async (id: TripId, block: boolean) => {
    if (state.activeTripId === id && state.status !== "error") return;
    const loadVersion = block ? beginBlockingLoad() : null;
    const selected = ++selectionVersion;
    stopLive();
    cellController?.abort();
    reconnectAttempt = 0;
    setState({
      activeTripId: id,
      status: "loading",
      error: null,
      cellOverlay: emptyCells(),
      cellStatus: "idle",
      stats: emptyStats(),
      statsStatus: "idle",
      health: [],
      healthRevision: 0n,
    });
    try {
      await refreshTrip(true);
      await Promise.all([state.settings.showStats ? loadStats() : Promise.resolve(), loadHealth()]);
      const wantsCells =
        state.settings.showPoints ||
        state.settings.showTowers ||
        state.settings.showLines ||
        state.settings.showCellCoverage;
      if (selected === selectionVersion && state.status === "ready" && wantsCells) await loadCells();
      else if (selected === selectionVersion && state.status === "ready") connectLive(id, selected);
    } finally {
      if (loadVersion !== null) endBlockingLoad(loadVersion);
    }
  };
  const selectTrip = (id: TripId) => selectTripInternal(id, true);
  const loadTrips = async () => {
    const version = beginBlockingLoad();
    listController?.abort();
    listController = new AbortController();
    setState({ status: "loading", error: null });
    try {
      const trips = await api.getTrips(listController.signal);
      if (listController.signal.aborted) return;
      setState("trips", reconcile(trips));
      setState("status", "ready");
      if (trips[0]) await selectTripInternal(trips[0].id, false);
    } catch (error) {
      if (!listController.signal.aborted)
        setState({ status: "error", error: error instanceof Error ? error.message : "Request failed" });
    } finally {
      endBlockingLoad(version);
    }
  };
  const toggleSetting = (key: SettingKey) => {
    setState("settings", key, (value) => !value);
    saveSettings({ ...state.settings }, storage);
    if (key === "showStats") {
      if (state.settings.showStats) {
        // eslint-disable-next-line solid/reactivity -- continuation runs only after a UI-triggered load.
        void loadStats().finally(() => {
          if (state.settings.showStats) restartLive();
        });
      } else {
        statsController?.abort();
        setState("stats", reconcile(emptyStats()));
        setState("statsStatus", "idle");
        restartLive();
      }
    }
    if (key === "showPoints" || key === "showTowers" || key === "showLines" || key === "showCellCoverage") {
      if (
        state.settings.showPoints ||
        state.settings.showTowers ||
        state.settings.showLines ||
        state.settings.showCellCoverage
      ) {
        if (state.cellStatus !== "ready") void loadCells();
        else restartLive();
      } else restartLive();
    }
  };
  const handleOnline = () => {
    if (state.liveStatus === "reconnecting" && state.activeTripId !== null)
      connectLive(state.activeTripId, selectionVersion);
  };
  window.addEventListener("online", handleOnline);
  const dispose = () => {
    disposed = true;
    stopLive();
    window.removeEventListener("online", handleOnline);
    listController?.abort();
    tripController?.abort();
    cellController?.abort();
    statsController?.abort();
  };
  return {
    state,
    loadTrips,
    selectTrip,
    refreshTrip: () => refreshTrip(false),
    toggleSetting,
    dismissError: () => setState("error", null),
    dispose,
  };
}
export type AppStore = ReturnType<typeof createAppStore>;
